import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext"; // Importa tu contexto
import Icon from "../ui/Icon";
import Eyebrow from "../ui/Eyebrow";
import SealChip from "../ui/SealChip";

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // 'idle', 'loading', 'success', 'error'
  const { resetPassword } = useAuth(); // Asume que tienes esta función en tu AuthContext

  const panelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const closeButtonRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await resetPassword(email);
      setStatus("success");
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("resetPassword error:", err);
      }
      setStatus("error");
    }
  };

  // Accesibilidad del diálogo modal: foco inicial, cierre con Escape,
  // focus trap (Tab/Shift+Tab) y restauración del foco al cerrar.
  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const panel = panelRef.current;
    const getFocusable = () =>
      panel
        ? Array.from(
            panel.querySelectorAll(
              'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
            )
          )
        : [];

    const focusable = getFocusable();
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (panel) {
      panel.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const items = getFocusable();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey) {
          if (
            document.activeElement === first ||
            !panel.contains(document.activeElement)
          ) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (
            document.activeElement === last ||
            !panel.contains(document.activeElement)
          ) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Al enviarse con éxito, mover el foco al botón principal de la pantalla de éxito
  useEffect(() => {
    if (status === "success" && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [status]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto bg-slate-900/50 backdrop-blur-sm">
      <div className="flex min-h-full items-center justify-center p-4 py-8">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="forgot-password-title"
          className="bg-white p-8 rounded-2xl max-w-sm w-full elev-3 animate-slideUp"
        >
          <Eyebrow className="mb-3">Palauta käyttöoikeus</Eyebrow>
          <h2
            id="forgot-password-title"
            className="text-2xl font-bold tracking-tight text-slate-900 mb-4"
          >
            Unohtuiko salasana?
          </h2>

          {status === "success" ? (
            <div className="text-center py-4" role="status" aria-live="polite">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200 mx-auto mb-4">
                <Icon name="check" className="w-6 h-6" />
              </span>
              <p className="text-emerald-600 font-bold mb-4">
                Viesti lähetetty!
              </p>
              <button
                ref={closeButtonRef}
                onClick={onClose}
                className="w-full py-3 bg-slate-100 hover:bg-slate-200 rounded-xl font-semibold text-slate-700 transition-colors"
              >
                Sulje
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-slate-500 text-sm">
                Anna sähköpostiosoitteesi, niin lähetämme ohjeet restablecerla.
              </p>

              {status === "error" && (
                <div
                  role="alert"
                  className="p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-3"
                >
                  <Icon
                    name="no-lock"
                    className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5"
                  />
                  <span className="text-sm text-rose-700">
                    Viestin lähetys epäonnistui. Tarkista osoite ja yritä
                    uudelleen.
                  </span>
                </div>
              )}

              <div>
                <label htmlFor="forgot-email" className="sr-only">
                  Sähköpostiosoite
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="sinun@sähköposti.com"
                  className="w-full p-3 border-2 border-slate-200 rounded-xl transition-colors focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={status === "loading"}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === "loading" ? "Lähetetään..." : "Lähetä linkki"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full text-slate-400 text-sm hover:text-slate-600 transition-colors"
              >
                Peruuta
              </button>

              <div className="pt-2 flex justify-center">
                <SealChip label="Turvallinen linkki" icon="shield" />
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
